/**
 * How New request writes a date, a time, a distance and a length —
 * `docs/new-request.md` §3 (Counts and units).
 *
 * Formatting only. Every number these print was worked out by the server
 * (rule 2); these turn it into the words the boards use, on both sides, so
 * the sheet and the confirmation text cannot spell a time two ways.
 */
import { parseDay } from '../calendar.js';
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
/** 0 = Sunday … 6 = Saturday, for a `YYYY-MM-DD`. */
export function weekdayOf(day) {
    const { year, month, day: date } = parseDay(day);
    return new Date(Date.UTC(year, month - 1, date, 12)).getUTCDay();
}
/** ISO weekday, 1 = Monday … 7 = Sunday — `users.work_days`. */
export const isoWeekdayOf = (day) => ((weekdayOf(day) + 6) % 7) + 1;
const short = (word) => word.slice(0, 3);
/** *Thu Oct 22* — lists, strips and cards. */
export function dayShort(day) {
    const { month, day: date } = parseDay(day);
    return `${short(WEEKDAYS[weekdayOf(day)])} ${short(MONTHS[month - 1])} ${date}`;
}
/** *Thu, Oct 22* — fields and messages. */
export function dayComma(day) {
    const { month, day: date } = parseDay(day);
    return `${short(WEEKDAYS[weekdayOf(day)])}, ${short(MONTHS[month - 1])} ${date}`;
}
/** *Thursday, October 22* — the email, the one place a date is written out (§12.2). */
export function dayLong(day) {
    const { month, day: date } = parseDay(day);
    return `${WEEKDAYS[weekdayOf(day)]}, ${MONTHS[month - 1]} ${date}`;
}
/** *Thursday*. */
export const weekdayLong = (day) => WEEKDAYS[weekdayOf(day)];
/** *Thu*. */
export const weekdayShort = (day) => short(WEEKDAYS[weekdayOf(day)]);
/** *Oct 22*. */
export function monthDay(day) {
    const { month, day: date } = parseDay(day);
    return `${short(MONTHS[month - 1])} ${date}`;
}
/** *Nov*, for *next month (Nov)*. */
export const monthShort = (day) => short(MONTHS[parseDay(day).month - 1]);
const meridiem = (minutes) => (Math.floor(minutes / 60) % 24) < 12 ? 'AM' : 'PM';
/** *10:00*, from minutes after midnight, no AM or PM. */
export function clockBare(minutes) {
    const hour24 = Math.floor(minutes / 60) % 24;
    const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${hour}:${String(minutes % 60).padStart(2, '0')}`;
}
/** *10:00 AM*. */
export const clock = (minutes) => `${clockBare(minutes)} ${meridiem(minutes)}`;
/** *8 AM*, *12 PM* — the hour rows of a day. */
export function hourLabel(minutes) {
    const hour24 = Math.floor(minutes / 60) % 24;
    return `${hour24 % 12 === 0 ? 12 : hour24 % 12} ${meridiem(minutes)}`;
}
/**
 * *10:00–11:30 AM*, or *11:30 AM–1:00 PM* when the two halves of the day
 * differ. `bare` drops both, for a sentence that already says the day:
 * *12:15–1:45 runs into Fox Hollow at 12:30.*
 */
export function clockRange(start, end, bare = false) {
    if (bare)
        return `${clockBare(start)}–${clockBare(end)}`;
    if (meridiem(start) === meridiem(end))
        return `${clockBare(start)}–${clock(end)}`;
    return `${clock(start)}–${clock(end)}`;
}
/** *between 10:00 and 10:30 AM*, the confirmation's arrival window. */
export function betweenWords(start, end) {
    if (meridiem(start) === meridiem(end))
        return `between ${clockBare(start)} and ${clock(end)}`;
    return `between ${clock(start)} and ${clock(end)}`;
}
/** *1.6 mi* — one decimal under 10, whole above (§3). */
export function milesWords(miles) {
    if (miles < 10)
        return `${(Math.round(miles * 10) / 10).toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
}
/** *8 min*, *1 h 30 min*, *2 h* — drives and lengths. */
export function minutesWords(minutes) {
    const whole = Math.round(minutes);
    if (whole < 60)
        return `${whole} min`;
    const hours = Math.floor(whole / 60);
    const rest = whole % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
}
/** Metres to miles. */
export const metresToMiles = (metres) => metres / 1609.344;
/** *Mon–Thu*, *Mon–Fri*, *Mon, Wed, Fri* — a person's working days (ISO 1–7). */
export function workDaysWords(days) {
    const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const sorted = [...new Set(days)].filter((day) => day >= 1 && day <= 7).sort((a, b) => a - b);
    if (sorted.length === 0)
        return 'no days';
    const contiguous = sorted.every((day, index) => index === 0 || day === sorted[index - 1] + 1);
    if (contiguous && sorted.length > 2) {
        return `${names[sorted[0] - 1]}–${names[sorted[sorted.length - 1] - 1]}`;
    }
    return sorted.map((day) => names[day - 1]).join(', ');
}
/** *8–5*, *7–4* — hours on the coverage grid (Board 11); *8:30–5* keeps the minutes. */
export function hoursShort(start, end) {
    const one = (minutes) => {
        const hour24 = Math.floor(minutes / 60);
        const hour = hour24 % 12 === 0 ? 12 : hour24 % 12;
        return minutes % 60 ? `${hour}:${String(minutes % 60).padStart(2, '0')}` : `${hour}`;
    };
    return `${one(start)}–${one(end)}`;
}
/** `'08:00'` or `'08:00:00'` to minutes after midnight. */
export function minutesOfTime(time) {
    const [hours, minutes] = time.split(':');
    return Number(hours) * 60 + Number(minutes ?? 0);
}
/** Initials for a name: *Tom Reyes* → *TR*. */
export function initialsOf(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '';
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
    return (first + last).toUpperCase();
}
//# sourceMappingURL=words.js.map